import {
  AddIcon,
  Alert,
  Button,
  Dialog,
  FormStack,
  Skeleton,
  Stack,
  TextField,
} from '@all-manager/design-system-v2';
import { Controller } from 'react-hook-form';
import DescriptionFieldOk from '../../../../components/modals/components/DescriptionFieldOk';
import StartEndDatesField from '../../../../components/modals/components/StartEndDatesField';
import DocumentsFormSection from '../../../documents/external-components/DocumentsFormSection/DocumentsFormSection';
import ProviderAutocomplete from '../../../providers/external-components/ProviderAutocomplete/ProviderAutocomplete';
import DeleteEventModal from '../DeleteEventModal/DeleteEventModal';
import useCreateUpdateEventModal from './useCreateUpdateEventModal';

type Props = {
  onClose: () => void;
  subTopicId: string;

  eventId?: string;
  providerId?: string;
  initialValues?: {
    title?: string;
    description?: string;
    date?: string;
    end_date?: string | null;
    all_day?: boolean;
    isImportant?: boolean;
    isDisplayedInCalendar?: boolean;
    provider_name?: string;
    contact_name?: string;
  };
  onSuccess?: () => void;
}

const FIELD_SKELETON_HEIGHT = 56;

export default function CreateUpdateEventModal(props: Props) {
  const {
    event,
    form,
    handleConfirm,
    isFormReady,
    isConfirmationPending,
    serverError,
    isModalToDeleteOpened,
    setIsModalToDeleteOpened,
    showDescription,
    showDocuments,
    revealDescription,
    revealDocuments,
  } = useCreateUpdateEventModal(props);

  const secondaryAction = {
    label: 'Annuler',
    onClick: () => props.onClose(),
    disabled: isConfirmationPending,
  };

  return (
    <>
      <Dialog
        open
        onClose={() => props.onClose()}
        onSubmit={form.handleSubmit(handleConfirm)}
        maxWidth="md"
        fullWidth
        title={props.eventId ? "Modifier l'événement" : 'Nouvel événement'}
        closeLabel="Fermer"
        actions={{
          primaryAction: {
            label: props.eventId ? "Enregistrer l'événement" : "Créer l'événement",
            type: 'submit',
            loading: isConfirmationPending,
            disabled: !isFormReady,
          },
          secondaryAction,
          tertiaryAction: props.eventId && event ? {
            label: 'Supprimer',
            onClick: () => setIsModalToDeleteOpened(true),
            disabled: isConfirmationPending,
          } : undefined,
        }}
      >
        {!isFormReady ? (
          <Stack spacing={1} aria-busy="true" aria-label="Chargement de l'événement">
            <Skeleton variant="rounded" height={FIELD_SKELETON_HEIGHT} />
            <Skeleton variant="rounded" height={FIELD_SKELETON_HEIGHT} />
            <Skeleton variant="rounded" height={FIELD_SKELETON_HEIGHT} />
            <Skeleton variant="rounded" height={FIELD_SKELETON_HEIGHT} />
          </Stack>
        ) : (
          <Stack spacing={3}>
            {serverError ? <Alert severity="error">{serverError}</Alert> : null}

            <FormStack>
              <Controller
                name="title"
                control={form.control}
                render={({ field, fieldState }) => (
                  <TextField
                    name={field.name}
                    inputRef={field.ref}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    value={field.value ?? ''}
                    label="Titre"
                    fullWidth
                    required
                    autoFocus
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <StartEndDatesField
                form={form}
                startDateName="date"
                endDateName="end_date"
                startDateLabel="Date de début"
                endDateLabel="Date de fin"
                allDayName="all_day"
                isRequiredStartDate
              />

              <Controller
                control={form.control}
                name="provider_id"
                render={({ field, fieldState }) => (
                  <ProviderAutocomplete
                    {...field}
                    label="Prestataire"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    defaultSearch={form.watch('provider_name')}
                    onChangeText={(value) => form.setValue('provider_name', value)}
                  />
                )}
              />

              <Controller
                name="contact_name"
                control={form.control}
                render={({ field, fieldState }) => (
                  <TextField
                    name={field.name}
                    inputRef={field.ref}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    value={field.value ?? ''}
                    label="Personne(s) présente(s)"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </FormStack>

            {showDescription ? (
              <DescriptionFieldOk
                form={form}
                name="description"
              />
            ) : null}

            {showDocuments ? (
              <Controller
                name="documents"
                control={form.control}
                render={({ field }) => (
                  <DocumentsFormSection
                    documents={field.value}
                    onChange={field.onChange}
                    subjectId={props.subTopicId}
                  />
                )}
              />
            ) : null}

            {!showDescription || !showDocuments ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                {!showDescription ? (
                  <Button variant="text" color="default" startIcon={<AddIcon />} onClick={() => revealDescription()}>
                    Ajouter une description
                  </Button>
                ) : null}
                {!showDocuments ? (
                  <Button variant="text" color="default" startIcon={<AddIcon />} onClick={() => revealDocuments()}>
                    Ajouter des documents
                  </Button>
                ) : null}
              </Stack>
            ) : null}
          </Stack>
        )}
      </Dialog>

      {isModalToDeleteOpened && event ? (
        <DeleteEventModal
          event={event}
          onClose={() => setIsModalToDeleteOpened(false)}
          onDeleted={() => props.onClose()}
        />
      ) : null}
    </>
  );
}
